import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import type { Product } from '../../../lib/supabase';

export default function FeaturedProducts() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [buttonStates, setButtonStates] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadFeaturedProducts();
  }, []);

  const loadFeaturedProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('featured', true)
        .eq('active', true)
        .limit(4);

      if (!error && data) {
        setProducts(data);
      }
    } catch (err) {
      console.error('Erro ao carregar produtos:', err);
    } finally {
      setLoading(false);
    }
  };

  const addToCart = (product: Product) => {
    const cart = JSON.parse(localStorage.getItem('cart') || '[]');
    const existingItem = cart.find((item: any) => item.id === product.id);

    if (existingItem) {
      existingItem.quantity += 1;
    } else {
      cart.push({
        id: product.id,
        name: product.name,
        price: product.price,
        image: product.images[0] || '',
        quantity: 1,
      });
    }

    localStorage.setItem('cart', JSON.stringify(cart));
    window.dispatchEvent(new Event('cartUpdated'));

    setButtonStates({ ...buttonStates, [product.id]: true });
    setTimeout(() => {
      setButtonStates({ ...buttonStates, [product.id]: false });
      window.REACT_APP_NAVIGATE('/cart');
    }, 500);
  };

  if (loading) {
    return (
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <i className="ri-loader-4-line text-4xl text-neon-cyan animate-spin"></i>
            <p className="mt-4 text-gray-400">Carregando produtos...</p>
          </div>
        </div>
      </section>
    );
  }

  if (products.length === 0) {
    return (
      <section className="py-20 bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <i className="ri-shopping-bag-line text-6xl text-gray-600 mb-4"></i>
            <p className="text-gray-400 text-lg">Nenhum produto em destaque no momento</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-black text-white mb-4">
            Produtos em <span className="text-transparent bg-clip-text bg-gradient-to-r from-neon-pink to-neon-cyan">Destaque</span>
          </h2>
          <p className="text-gray-400 text-lg">Os melhores componentes para seu setup gamer</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {products.map((product) => (
            <div
              key={product.id}
              className="bg-gray-800 rounded-xl overflow-hidden border border-gray-700 hover:border-neon-cyan transition-all group"
            >
              <div className="relative h-64 w-full overflow-hidden bg-gray-900">
                <img
                  src={product.images[0] || 'https://public.readdy.ai/ai/img_res/e44ad9009ca3bfc3e00a733f3aeda182.jpg'}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-300"
                />
                {product.old_price && product.old_price > product.price && (
                  <div className="absolute top-4 right-4 bg-gradient-to-r from-neon-pink to-neon-cyan text-white px-3 py-1 rounded-full text-sm font-bold">
                    -{Math.round(((product.old_price - product.price) / product.old_price) * 100)}%
                  </div>
                )}
              </div>

              <div className="p-6">
                <h3 className="text-white font-bold text-lg mb-2 line-clamp-2">{product.name}</h3>
                {product.brand && (
                  <p className="text-gray-400 text-sm mb-3">{product.brand}</p>
                )}
                
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex text-yellow-400">
                    {[...Array(5)].map((_, i) => (
                      <i key={i} className={`${i < Math.floor(product.rating) ? 'ri-star-fill' : 'ri-star-line'} text-sm`}></i>
                    ))}
                  </div>
                  <span className="text-gray-400 text-sm">({product.reviews})</span>
                </div>

                <div className="mb-4">
                  {product.old_price && product.old_price > product.price && (
                    <p className="text-gray-500 line-through text-sm">
                      R$ {product.old_price.toFixed(2)}
                    </p>
                  )}
                  <p className="text-neon-cyan text-2xl font-black">
                    R$ {product.price.toFixed(2)}
                  </p>
                </div>

                <button
                  onClick={() => addToCart(product)}
                  className="w-full bg-gradient-to-r from-neon-pink to-neon-cyan hover:shadow-neon-pink text-white py-3 rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer"
                >
                  {buttonStates[product.id] ? 'Adicionado!' : 'Adicionar ao Carrinho'}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
